// ContentLoader.js - Modified to support beta content and app configuration
// Service for loading game content dynamically from the public folder

/**
 * Load the application configuration for the specified company and language
 * @param {string} language - Language code (e.g. 'en', 'nl')
 * @param {string} companyId - Optional company identifier for custom branding
 * @param {boolean} beta - Whether to load from beta content
 * @returns {Promise<Object>} - The app configuration object
 */
export const loadAppConfiguration = async (language = "en", companyId = null, beta = false) => {
  try {
    // Default to English if requested language isn't available
    const effectiveLanguage = language || "en"

    // Determine the base content path based on beta mode
    const basePath = beta ? `/content-beta` : `/content`

    // First try to load the main app config
    let configPath = `${basePath}/main/app-config.json`

    // If a specific company is requested, try to load company-specific config
    if (companyId) {
      configPath = `${basePath}/main/${companyId}/app-config.json`
    }

    console.log(`Loading app configuration from ${configPath}`)

    let response = await fetch(configPath)

    // If company-specific config doesn't exist, fall back to default
    if (!response.ok && companyId) {
      console.info(`No company-specific config for ${companyId}, falling back to default`)
      configPath = `${basePath}/main/app-config.json`
      response = await fetch(configPath)
    }

    if (!response.ok) {
      throw new Error(`Failed to load app config: ${response.status} ${response.statusText}`)
    }

    // Parse the base configuration
    const baseConfig = await response.json()

    // Now load language-specific content if available (for texts, assets, etc.)
    try {
      const langConfigPath = `${basePath}/main/${effectiveLanguage}/localization.json`
      const langResponse = await fetch(langConfigPath)

      if (langResponse.ok) {
        const langConfig = await langResponse.json()

        // Deep merge the language-specific content with the base config
        return deepMerge(baseConfig, langConfig)
      }
    } catch (langError) {
      console.warn(`Failed to load language-specific config for ${effectiveLanguage}`, langError)
      // Continue with base config only
    }

    return baseConfig
  } catch (error) {
    console.error(`Error loading app configuration:`, error)

    // Return minimal fallback configuration
    return {
      branding: {
        logo: "/images/techtok-logo.png",
        logoAlt: "Logo",
        logoLink: "https://techtok.me",
        accentColor: "#5F29FF",
        faviconPath: "/favicon.ico",
        logoLight: null, // Removed reference to undeclared config variable
      },
      _source: "fallback",
    }
  }
}

// Helper function to deep merge objects (for merging config files)
function deepMerge(target, source) {
  const output = { ...target }

  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] })
        } else {
          output[key] = deepMerge(target[key], source[key])
        }
      } else {
        Object.assign(output, { [key]: source[key] })
      }
    })
  }

  return output
}

// Helper to check if value is an object
function isObject(item) {
  return item && typeof item === "object" && !Array.isArray(item)
}

/**
 * Load a game's configuration and list of sections
 * @param {string} gameId - The ID of the game to load
 * @param {string} language - Language code (e.g. 'en', 'nl')
 * @param {boolean} beta - Whether to load from beta content
 * @returns {Promise<Object>} - The game configuration and sections
 */
export const loadGameConfiguration = async (gameId, language, beta = false) => {
  try {
    // Default to English if requested language isn't available
    const effectiveLanguage = language || "en"

    // Determine the content path based on whether we're in beta mode
    // Beta content is now at the same level as content directory
    const contentPath = beta ? `/content-beta/${effectiveLanguage}` : `/content/${effectiveLanguage}`

    console.log(`Loading game configuration from ${contentPath}/${gameId}/index.json, beta mode: ${beta}`)

    // Fetch the game index file
    const response = await fetch(`${contentPath}/${gameId}/index.json`)

    if (!response.ok) {
      // If language-specific version doesn't exist, try English as fallback
      if (!beta && effectiveLanguage !== "en") {
        console.info(`No ${effectiveLanguage} version for ${gameId}, trying English fallback`)
        return loadGameConfiguration(gameId, "en", false)
      }

      // If beta content doesn't exist, try regular content
      if (beta) {
        console.info(`No beta version for ${gameId}, trying regular content`)
        return loadGameConfiguration(gameId, language, false)
      }

      throw new Error(`Failed to load game ${gameId}: ${response.status} ${response.statusText}`)
    }

    // Log the raw response for debugging
    const responseText = await response.text()
    console.log(`Raw index.json response for ${gameId}:`, responseText)

    // Parse the JSON (after logging the raw text)
    let gameData
    try {
      gameData = JSON.parse(responseText)
    } catch (parseError) {
      console.error(`JSON parse error for ${gameId}/index.json:`, parseError)
      throw new Error(`Failed to parse game data: ${parseError.message}`)
    }

    if (!gameData || !gameData.sections || !Array.isArray(gameData.sections)) {
      console.error(`Invalid game data format for ${gameId}:`, gameData)
      throw new Error(`Invalid game data format for ${gameId}`)
    }

    // The sections array needs to be loaded separately
    const sectionsData = await Promise.all(
      gameData.sections.map(async (sectionFile) => {
        try {
          const sectionResponse = await fetch(`${contentPath}/${gameId}/${sectionFile}`)

          if (!sectionResponse.ok) {
            console.warn(`Failed to load section ${sectionFile} for game ${gameId}: ${sectionResponse.status}`)
            return null
          }

          // Log the raw section response for debugging
          const sectionText = await sectionResponse.text()
          console.log(`Raw section response for ${sectionFile}:`, sectionText.substring(0, 200) + "...")

          // Parse the section JSON
          try {
            return JSON.parse(sectionText)
          } catch (sectionParseError) {
            console.error(`JSON parse error for section ${sectionFile}:`, sectionParseError)
            return null
          }
        } catch (sectionError) {
          console.error(`Error fetching section ${sectionFile}:`, sectionError)
          return null
        }
      }),
    )

    // Filter out any sections that failed to load
    const validSections = sectionsData.filter((section) => section !== null)

    if (validSections.length === 0) {
      console.warn(`No valid sections loaded for game ${gameId}`)
    }

    // Add a beta flag to the game config if we're in beta mode
    if (beta) {
      gameData.gameConfig.isBeta = true
    }

    return {
      gameConfig: gameData.gameConfig,
      sections: validSections,
    }
  } catch (error) {
    console.error(`Error loading game ${gameId}:`, error)
    throw error
  }
}

/**
 * Get a list of available games with their basic metadata
 * @param {string} language - Language code (e.g. 'en', 'nl')
 * @param {boolean} beta - Whether to load beta games
 * @returns {Promise<Array>} - Array of game objects with basic metadata
 */
export const getAvailableGames = async (language = "en", beta = false) => {
  console.log(`Getting available games for language: ${language}, beta: ${beta}`)

  try {
    // Determine the content path based on whether we're in beta mode
    const contentPath = beta ? `/content-beta/${language}` : `/content/${language}`

    // Load the catalog of available games
    const response = await fetch(`${contentPath}/directory.json`)

    if (!response.ok) {
      if (beta) {
        console.warn("No beta directory found, returning empty array")
        return []
      }

      // If language-specific version doesn't exist, try English fallback
      if (language !== "en") {
        console.info(`No ${language} directory, trying English fallback`)
        return getAvailableGames("en", beta)
      }

      console.error(`Failed to load directory: ${response.status} ${response.statusText}`)
      return []
    }

    // Parse the directory
    const directoryData = await response.json()
    const gameIds = directoryData.games || []

    console.log(`Loaded game IDs:`, gameIds)

    // For each game ID, try to load some basic metadata
    const gamesWithMetadata = await Promise.all(
      gameIds.map(async (id) => {
        try {
          // Try to fetch the game index to get real metadata
          const response = await fetch(`${contentPath}/${id}/index.json`)
          if (response.ok) {
            const text = await response.text()
            const data = JSON.parse(text)
            if (data && data.gameConfig) {
              return {
                id,
                title: data.gameConfig.title || id,
                description: data.gameConfig.description || "",
                emoji: data.gameConfig.emoji || "📱",
                iconImage: data.gameConfig.iconImage || null,
                bgColor: data.gameConfig.bgColor || "bg-[#EFEAFC]",
                category: data.gameConfig.category || "",
                difficulty: data.gameConfig.difficulty || "Beginner",
                estimatedDuration: data.gameConfig.estimatedDuration || "90-120 min",
                ageRange: data.gameConfig.ageRange || "8-12",
                tags: data.gameConfig.tags || [],
                featured: data.gameConfig.featured || false,
                featuredText: data.gameConfig.featuredText || "Featured",
                comingSoon: data.gameConfig.comingSoon || false,
                locked: data.gameConfig.locked || false,
                isBeta: beta,
                language,
              }
            }
          }

          // Fallback to basic info if we can't load the metadata
          return {
            id,
            title: id
              .split("-")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" "),
            emoji: "📱",
            bgColor: "bg-[#EFEAFC]",
            difficulty: "Beginner",
            ageRange: "8-12",
            isBeta: beta,
            language,
          }
        } catch (error) {
          console.warn(`Error loading metadata for game ${id}:`, error)
          // Return basic info as fallback
          return {
            id,
            title: id
              .split("-")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" "),
            emoji: "📱",
            bgColor: "bg-[#EFEAFC]",
            isBeta: beta,
            language,
          }
        }
      }),
    )

    console.log(`Processed games with metadata:`, gamesWithMetadata)
    return gamesWithMetadata
  } catch (error) {
    console.error(`Error getting available games:`, error)
    // Minimal fallback
    return beta
      ? []
      : [
          {
            id: "app-designer",
            title: "App Designer",
            emoji: "📱",
            bgColor: "bg-[#EFEAFC]",
            language,
          },
        ]
  }
}

/**
 * Load the catalog of available games from directory.json
 * @param {string} language - Language code (e.g. 'en', 'nl')
 * @param {boolean} beta - Whether to load beta games
 * @returns {Promise<Array>} - Array of available games
 */
export const loadGamesCatalog = async (language, beta = false) => {
  try {
    // Default to English if requested language isn't available
    const effectiveLanguage = language || "en"

    // Determine the content path based on whether we're in beta mode
    const contentPath = beta ? `/content-beta/${effectiveLanguage}` : `/content/${effectiveLanguage}`

    // Fetch the directory file
    console.log(`Fetching directory from ${contentPath}/directory.json`)
    const response = await fetch(`${contentPath}/directory.json`)

    if (!response.ok) {
      if (beta) {
        console.warn("No beta directory found, returning empty array")
        return []
      }

      // If language-specific version doesn't exist, try English fallback
      if (effectiveLanguage !== "en") {
        console.info(`No ${effectiveLanguage} directory, trying English fallback`)
        return loadGamesCatalog("en", beta)
      }
      throw new Error(`Failed to load directory: ${response.status} ${response.statusText}`)
    }

    // Log the raw directory response for debugging
    const directoryText = await response.text()
    console.log(`Raw directory.json response:`, directoryText)

    // Parse the directory JSON
    let directoryData
    try {
      directoryData = JSON.parse(directoryText)
    } catch (parseError) {
      console.error(`JSON parse error for directory.json:`, parseError)
      throw new Error(`Failed to parse directory data: ${parseError.message}`)
    }

    // Return the list of games
    if (!directoryData || !directoryData.games) {
      console.warn(`Invalid directory data format:`, directoryData)
      return beta ? [] : ["app-designer"] // Fallback to at least include app-designer for non-beta
    }

    return directoryData.games
  } catch (error) {
    console.error("Error loading games directory:", error)
    // Return empty array for beta mode, basic games as fallback for regular mode
    return beta ? [] : ["app-designer"]
  }
}

/**
 * Check if a beta game is available
 * @param {string} lessonId - The ID of the lesson to check
 * @returns {Promise<boolean>} - Whether the beta lesson exists
 */
export const checkBetaLesson = async (lessonId) => {
  try {
    // Extract gameId from lessonId (assuming format like "game-id-section-step")
    const gameId = lessonId.split("-")[0]

    // Instead of calling an API, check directly for the file
    const response = await fetch(`/content-beta/${gameId}/index.json`)
    return response.ok
  } catch (error) {
    console.error(`Error checking beta lesson ${lessonId}:`, error)
    return false
  }
}

/**
 * Get the correct path for downloadable files based on game and language
 * @param {string} gameId - The ID of the current game
 * @param {string} language - Language code (e.g. 'en', 'nl')
 * @param {string} filePath - Path to the file, either absolute or relative
 * @param {boolean} beta - Whether to load from beta content
 * @returns {string} The complete path to the file
 */
export const getDownloadableFilePath = (gameId, language, filePath, beta = false) => {
  // If it's already an absolute path, return it as is
  if (filePath.startsWith("http") || filePath.startsWith("/")) {
    return filePath
  }

  // Ensure we have a valid language code
  const effectiveLanguage = language || "en"

  // Determine the content path based on whether we're in beta mode
  const contentPath = beta ? `/content-beta/${effectiveLanguage}` : `/content/${effectiveLanguage}`

  // Otherwise, construct path based on game and language
  return `${contentPath}/${gameId}/downloads/${filePath}`
}

function checkImageExists(url) {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve(true)
    img.onerror = () => resolve(false)
    img.src = url
  })
}

export default {
  loadGameConfiguration,
  loadGamesCatalog,
  getAvailableGames,
  checkBetaLesson,
  getDownloadableFilePath,
  loadAppConfiguration,
}
